import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Spinner } from "react-bootstrap";
import { AppContext } from "../context/AppContext";
import fetchData from "../utils/fetchData";
import getCookie from "../utils/getCookie";
import { printInvoice } from "../utils/printInvoice";
import Delete from "./Delete";
import styles from "../../styles/scss/module/StoreOrder.module.scss";

const statusBadges = {
  paid: "bg-success text-white",
  pending: "bg-warning text-dark",
  failed: "bg-danger text-white",
  cancelled: "bg-danger text-white",
};

const money = (n) =>
  `৳${Number(n || 0).toLocaleString("en-BD", { maximumFractionDigits: 2 })}`;

const StoreOrderView = () => {
  const { setAlert } = useContext(AppContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDelete, setDelete] = useState(false);
  const [dueForm, setDueForm] = useState({
    dueAmount: "",
    dueCollectedAmount: "",
    dueNote: "",
    dueStatus: "pending",
  });
  const [savingDue, setSavingDue] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await fetchData.get(`/storeOrder/${id}`, {
          headers: { Authorization: `Bearer ${getCookie("eyevaryToken")}` },
        });
        const o = data?.storeOrder;
        setOrder(o);
        setDueForm({
          dueAmount:
            o?.dueAmount ??
            Math.max(0, (o?.fullAmount || 0) - (o?.payableAmount || 0)),
          dueCollectedAmount: o?.dueCollectedAmount ?? 0,
          dueNote: o?.dueNote || "",
          dueStatus:
            o?.dueStatus && o.dueStatus !== "n/a" ? o.dueStatus : "pending",
        });
      } catch (error) {
        console.log({ ...error }, error);
        setAlert({
          show: true,
          variant: "danger",
          message:
            error?.response?.data?.message || "Failed to load order details.",
          icon: "mdi mdi-close",
          duration: 5,
        });
      }
      setLoading(false);
    };
    id && load();
  }, [id]);

  const handleDueChange = ({ target }) => {
    setDueForm({ ...dueForm, [target.name]: target.value });
  };

  const handleSaveDue = async (e) => {
    e.preventDefault();
    setSavingDue(true);
    try {
      const { data } = await fetchData.patch(
        `/storeOrder/${id}/due`,
        {
          dueAmount: dueForm.dueAmount,
          dueCollectedAmount: dueForm.dueCollectedAmount,
          dueNote: dueForm.dueNote,
          dueStatus: dueForm.dueStatus,
        },
        {
          headers: { Authorization: `Bearer ${getCookie("eyevaryToken")}` },
        },
      );
      setOrder(data?.storeOrder);
      setAlert({
        show: true,
        variant: "success",
        message: "Due amount updated successfully.",
        icon: "mdi mdi-check",
        duration: 5,
      });
    } catch (error) {
      console.log({ ...error }, error);
      setAlert({
        show: true,
        variant: "danger",
        message:
          error?.response?.data?.message ||
          "Failed to update due amount. Please try again.",
        icon: "mdi mdi-close",
        duration: 5,
      });
    }
    setSavingDue(false);
  };

  if (loading)
    return (
      <section className="content-wrapper text-center py-5">
        <Spinner animation="border" variant="danger" />
      </section>
    );

  if (!order)
    return (
      <section className="content-wrapper">
        <Card>
          <Card.Body>
            Order not found.{" "}
            <Link to="/store-orders" className="text-danger">
              Back to orders
            </Link>
          </Card.Body>
        </Card>
      </section>
    );

  const isPreOrder = !!order.isPreOrder;
  const advancePercent = order.advancePercent || 10;
  const fullAmount = Number(order.fullAmount || 0);
  const payableAmount = Number(order.payableAmount || 0);
  const baseLine = Number(order.unitPrice || 0) * Number(order.qty || 1);
  const vatAmount = Number(order.vatAmount || 0);
  const taxAmount = Number(order.taxAmount || 0);
  const discountAmount = Number(order.discountAmount || 0);
  const dueAmount = Number(
    order.dueAmount ?? Math.max(0, fullAmount - payableAmount),
  );
  const dueCollected = Number(order.dueCollectedAmount || 0);
  const remainingDue = Math.max(0, dueAmount - dueCollected);
  const orderDateTime = new Date(
    order.orderedAtClient || order.createdAt || Date.now(),
  ).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const status = String(order.paymentStatus || "pending").toUpperCase();
  const statusClass =
    order.paymentStatus === "paid"
      ? styles.badge_ok
      : order.paymentStatus === "pending"
        ? styles.badge_warn
        : styles.badge_bad;

  return (
    <section className={`content-wrapper ${styles.invoice_page}`}>
      <Delete
        isModal={isDelete}
        setModal={setDelete}
        id={id}
        onDeleted={() => navigate("/store-orders")}
      />
      <div className={`${styles.action_bar} no-print`}>
        <div>
          <Button
            as={Link}
            to="/store-orders"
            variant="outline-secondary"
            size="sm"
          >
            <i className="mdi mdi-arrow-left me-1"></i> Back to Orders
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span
            className={`badge px-3 py-2 ${
              statusBadges[order.paymentStatus] || "bg-secondary"
            }`}
          >
            Status: {status}
          </span>
          <Button
            variant="danger"
            size="sm"
            onClick={() => printInvoice(order)}
          >
            <i className="mdi mdi-printer me-1"></i> Download / Print Invoice
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => setDelete(true)}
          >
            <i className="mdi mdi-delete me-1"></i> Delete
          </Button>
        </div>
      </div>

      <div className={styles.invoice_paper}>
        <div className={styles.inv_top}>
          <div>
            <div className={styles.inv_brand}>Eyevary Limited</div>
            <p className={styles.inv_muted}>
              (Temporary): House 458, Road 6, Avenue 5, DOHS Mirpur, Dhaka,
              Bangladesh
              <br />
              Phone: +8801848080808 · Email: store@eyevary.com
              <br />
              Website: www.eyevary.com
            </p>
          </div>
          <div className={styles.inv_right}>
            <div className={styles.inv_title}>INVOICE</div>
            <p className={styles.inv_muted}>
              <strong>Invoice No:</strong> {order.tranId}
              <br />
              <strong>Order date &amp; time:</strong> {orderDateTime}
              <br />
              <strong>Type:</strong>{" "}
              {isPreOrder
                ? `Pre-Order (${advancePercent}% advance)`
                : "Standard purchase"}
            </p>
            <span className={`${styles.inv_badge} ${statusClass}`}>
              {status}
            </span>
          </div>
        </div>

        <div className={styles.inv_grid}>
          <div className={styles.inv_box}>
            <h4>Bill to</h4>
            <strong>{order.buyer?.name}</strong>
            <p className={styles.inv_muted}>
              Phone: {order.buyer?.phone}
              <br />
              Email: {order.buyer?.email}
              <br />
              Address: {order.buyer?.address}
            </p>
          </div>
          <div className={styles.inv_box}>
            <h4>Payment summary</h4>
            <p className={styles.inv_muted}>
              Method: {isPreOrder ? "SSLCommerz (advance)" : "SSLCommerz"}
              <br />
              Warranty: {order.product?.warrantyPeriod || "N/A"}
              <br />
              Qty: {order.qty || 1}
              {order.dueStatus && order.dueStatus !== "n/a" && (
                <>
                  <br />
                  Due status: {String(order.dueStatus).toUpperCase()}
                </>
              )}
            </p>
          </div>
        </div>

        <table className={styles.inv_table}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className={styles.inv_item_title}>
                  {order.product?.title}
                </div>
                {order.product?.warrantyPeriod && (
                  <div className={styles.inv_item_sub}>
                    Warranty: {order.product.warrantyPeriod}
                  </div>
                )}
                {!!order.discount && (
                  <div className={styles.inv_item_sub}>
                    Discount: {order.discount}%
                  </div>
                )}
              </td>
              <td>{order.qty || 1}</td>
              <td>{money(order.unitPrice || fullAmount)}</td>
              <td>{money(fullAmount)}</td>
            </tr>
          </tbody>
        </table>

        <div className={styles.inv_totals}>
          <div className={styles.inv_total_row}>
            <span>Price</span>
            <span>{money(baseLine)}</span>
          </div>
          {!!order.vatPercent && (
            <div className={styles.inv_total_row}>
              <span>VAT ({order.vatPercent}%)</span>
              <span>{money(vatAmount)}</span>
            </div>
          )}
          {!!order.taxPercent && (
            <div className={styles.inv_total_row}>
              <span>Tax ({order.taxPercent}%)</span>
              <span>{money(taxAmount)}</span>
            </div>
          )}
          {!!order.discount && (
            <div className={styles.inv_total_row}>
              <span>Discount ({order.discount}%)</span>
              <span>-{money(discountAmount)}</span>
            </div>
          )}
          <div className={styles.inv_total_row}>
            <span>Total price</span>
            <span>{money(fullAmount)}</span>
          </div>
          {isPreOrder && (
            <>
              <div className={styles.inv_total_row}>
                <span>Advance paid ({advancePercent}%)</span>
                <span className={styles.inv_accent}>
                  {money(payableAmount)}
                </span>
              </div>
              <div className={styles.inv_total_row}>
                <span>Due amount</span>
                <span>{money(dueAmount)}</span>
              </div>
              <div className={styles.inv_total_row}>
                <span>Due collected</span>
                <span>{money(dueCollected)}</span>
              </div>
              <div className={styles.inv_total_row}>
                <span>Due remaining</span>
                <span>{money(remainingDue)}</span>
              </div>
            </>
          )}
          <div className={`${styles.inv_total_row} ${styles.inv_grand}`}>
            <span>Amount paid online</span>
            <span>{money(payableAmount || fullAmount)}</span>
          </div>
        </div>

        <p className={styles.inv_footer}>
          {isPreOrder
            ? `Pre-order invoice. Advance ${money(
                payableAmount,
              )} (${advancePercent}%) paid online. Remaining due ${money(
                remainingDue,
              )} payable later / on delivery.`
            : "Thank you for shopping with Eyevary Limited. For support: store@eyevary.com"}
        </p>
      </div>

      {isPreOrder && (
        <Card className="mt-4 no-print">
          <Card.Body>
            <Card.Title className="mb-3">Pre-order due amount</Card.Title>
            <p className="text-muted small">
              Set or update the remaining due for this pre-order, and record how
              much has been collected later.
            </p>
            <form onSubmit={handleSaveDue}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Due amount (৳)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    name="dueAmount"
                    value={dueForm.dueAmount}
                    onChange={handleDueChange}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Collected so far (৳)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    name="dueCollectedAmount"
                    value={dueForm.dueCollectedAmount}
                    onChange={handleDueChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Due status</label>
                  <select
                    className="form-select"
                    name="dueStatus"
                    value={dueForm.dueStatus}
                    onChange={handleDueChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="partial">Partial</option>
                    <option value="settled">Settled</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Note (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="dueNote"
                    value={dueForm.dueNote}
                    onChange={handleDueChange}
                    placeholder="e.g. Collected cash on delivery"
                  />
                </div>
                <div className="col-12">
                  <Button type="submit" variant="danger" disabled={savingDue}>
                    {savingDue ? (
                      <>
                        Saving… <Spinner animation="border" size="sm" />
                      </>
                    ) : (
                      "Save due amount"
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}
    </section>
  );
};

export default StoreOrderView;
